import { useTranslation } from "react-i18next";
import { Video, MapPin, Calendar, Clock, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../../shared/ui/icons/SubjectKindIcon";
import { Link } from "react-router";
import Button from "../../../../../shared/ui/button/Button";
import { Session, getSessionPatientName } from "../../../domain/Session";
import { getAvatarColor } from "../../../../../shared/utils/avatar";
import { formatTherapyTypes } from "../../../../../shared/domain/therapyTypes";
import { useAiInsightsGate } from "../../../../ai_insights";
import { AnalysisStatusBadge, PaymentStatusBadge, SessionProgressBadge } from "../SessionStatusBadges";
import SessionStatusControls from "./SessionStatusControls";

interface SessionViewBasicProps {
    session: Session;
    onEdit: () => void;
    /** Given, the session and payment statuses can be set here by hand. */
    onSessionChange?: (changes: Partial<Session>) => void;
}

export default function SessionViewBasic({ session, onEdit, onSessionChange }: SessionViewBasicProps) {
    const { t, i18n } = useTranslation();
    const { adopted: showAnalysis } = useAiInsightsGate();

    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "—";

        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();

        if (i18n.language.startsWith("es")) {
            return `${day}-${month}-${year}`;
        }
        return `${year}-${month}-${day}`;
    };

    const meta = [
        { key: "date", icon: <Calendar className="w-4 h-4" aria-hidden="true" />, label: t("session.sessionDate"), value: formatDate(session.date) },
        { key: "hour", icon: <Clock className="w-4 h-4" aria-hidden="true" />, label: t("session.time"), value: session.hour || "—" },
        { key: "duration", icon: null, label: t("session.duration"), value: session.duration ? `${session.duration} ${t("common.minutes")}` : "—" },
        {
            key: "location",
            icon:
                session.ubication === "online" ? <Video className="w-4 h-4 text-accent-text" aria-hidden="true" />
                : session.ubication === "in person" ? <MapPin className="w-4 h-4 text-orange-500" aria-hidden="true" />
                : null,
            label: t("session.location"),
            value: session.ubication ? t(`session.locationOptions.${session.ubication.replace(" ", "_")}`) : "—",
        },
    ];

    return (
        <section
            aria-labelledby="session-header-title"
            className="glass-effect glass-border border rounded-2xl p-5 sm:p-6 shadow-theme-sm"
        >
            {/* The page's own heading already carries the session's title;
                repeating it here put it on screen twice, as two h1s. */}
            <h2 id="session-header-title" className="sr-only">{t("session.basicInfo")}</h2>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-3">
                {session.group_patient_id ? (
                    <div className="flex flex-col gap-1.5">
                        <Link to={`/group-patient/${session.group_patient_id}`} className="flex items-center gap-2.5 hover:bg-gray-50 dark:hover:bg-white/5 p-1.5 -ml-1.5 rounded-xl transition-all w-fit group">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white ${getAvatarColor(session.group_patient_id)}`}>
                                <GroupOfPeopleIcon className="w-4 h-4" aria-hidden="true" />
                            </div>
                            <span className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                                {getSessionPatientName(session) || "—"}
                            </span>
                        </Link>
                        {(session.group_members ?? []).length > 0 && (
                            <ul className="flex flex-wrap gap-1.5" aria-label={t("patient.group.members")}>
                                {(session.group_members ?? []).map((member) => (
                                    <li key={member.id}>
                                        <Link
                                            to={`/patient/${member.id}`}
                                            className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-brand-50 hover:text-brand-700 dark:bg-white/5 dark:text-gray-300"
                                        >
                                            {member.first_name} {member.last_name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                ) : session.type === 'couple' ? (
                    <Link to={`/couple-patient/${session.couple_patient_id}`} className="flex items-center gap-2.5 hover:bg-gray-50 dark:hover:bg-white/5 p-1.5 -ml-1.5 rounded-xl transition-all w-fit group">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs group-hover:bg-brand-100 dark:group-hover:bg-brand-500/20 transition-colors text-white ${getAvatarColor(session.couple_patient_id || "")}`}>
                            <Users className="w-4 h-4" />
                        </div>
                        <span className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                            {session.patient_name || "—"}
                        </span>
                    </Link>
                ) : (
                    <Link to={`/patient/${session.patient_id}`} className="flex items-center gap-2.5 hover:bg-gray-50 dark:hover:bg-white/5 p-1.5 -ml-1.5 rounded-xl transition-all w-fit group">
                        <div className="w-8 h-8 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold text-xs group-hover:bg-brand-100 dark:group-hover:bg-brand-500/20 transition-colors">
                            {session.patient_name?.[0].toUpperCase() || "P"}
                        </div>
                        <span className="font-semibold text-gray-800 dark:text-gray-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                            {session.patient_name
                                ? `${session.patient_name} ${session.patient_last_name || ""}`
                                : "—"}
                        </span>
                    </Link>
                )}
                </div>
                <Button size="sm" variant="outline" className="shrink-0 text-content-tertiary hover:text-brand-500 hover:bg-brand-50" onClick={onEdit}>
                    {t("common.edit")}
                </Button>
            </div>

            {/* When and where: one line that wraps, each value labelled for screen readers. */}
            <dl className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-content-secondary">
                {meta.map((item) => (
                    <div key={item.key} className="flex items-center gap-1.5">
                        <dt className="sr-only">{item.label}</dt>
                        {item.icon && <span className="text-content-tertiary">{item.icon}</span>}
                        <dd className="font-medium">{item.value}</dd>
                    </div>
                ))}
            </dl>

            {/* Where it stands: took place, paid, and — for those who use AI — its analysis. */}
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 dark:border-white/5">
                {onSessionChange ? (
                    <SessionStatusControls
                        sessionId={session.id}
                        sessionStatus={session.session_status ?? "pending"}
                        paymentStatus={session.payment_status ?? "pending"}
                        cancelledBy={session.cancelled_by}
                        onChange={onSessionChange}
                    />
                ) : (
                    <>
                        <SessionProgressBadge status={session.session_status} />
                        <PaymentStatusBadge status={session.payment_status} />
                    </>
                )}
                {showAnalysis && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-content-tertiary">
                        <span>{t("session.analysisStatus")}:</span>
                        <AnalysisStatusBadge status={session.status} skippedReason={session.analysis_skipped_reason} />
                    </span>
                )}
            </div>

            {/* The rest of what describes it, secondary. */}
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:flex sm:flex-wrap">
                <div className="flex flex-col gap-0.5">
                    <dt className="text-xs font-semibold text-content-tertiary">{t("session.sessionType")}</dt>
                    <dd className="font-medium text-content-secondary">
                        {session.type ? t(`session.typeOptions.${session.type}`) : "—"}
                    </dd>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                    <dt className="text-xs font-semibold text-content-tertiary">{t("session.therapyType")}</dt>
                    <dd className="font-medium text-content-secondary break-words">
                        {formatTherapyTypes(t, session.therapy_type) || "—"}
                    </dd>
                </div>
            </dl>
        </section>
    );
}
